import React, { useEffect, useState } from 'react';
import { StylesConfig, SingleValue, ActionMeta } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { t } from 'i18next';
import TimeTaxSupportSystemClient from '../../system/TimeTaxSupportSystemClient';

const normalizeString = (str: string = ''): string =>
  str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const filterOption = (option: { label: string; value: string }, inputValue: string) => {
  const normalizedLabel = normalizeString(option.label);
  const normalizedInput = normalizeString(inputValue);
  return normalizedLabel.includes(normalizedInput);
};

export interface OptionPais {
  key: string;
  label: string;
  new?: boolean;
}

interface PaisSelectorProps {
  system: TimeTaxSupportSystemClient;
  label?: string;
  defaultValue?: string | null;
  onChangeSelected: (selected: OptionPais | null) => void;
  disabled?: boolean;
  placeholder?: string;
  styles?: React.CSSProperties;
  className?: string;
}

export const customStylesReactSelect: StylesConfig<OptionPais, false> = {
  control: (provided) => ({
    ...provided,
    minHeight: 36,
    borderColor: 'rgb(96, 94, 92)',
    ':focus-within': {
      borderColor: 'rgb(0, 120, 212)',
      boxShadow: '0 0 0 1px rgb(0, 120, 212)',
    },
  }),
  menu: (provided) => ({ ...provided, zIndex: 1000 }),
  option: (base, { isSelected, isFocused }) => {
    return {
      ...base,
      ':hover': {
        background: 'rgb(243, 242, 241)',
        color: 'rgb(32, 31, 30)',
      },
      background: isSelected && isFocused ? 'rgb(243, 242, 241)' : '#ffffff',
      color: isSelected && isFocused ? 'rgb(32, 31, 30)' : '#000',
    };
  },
};

const PaisSelector: React.FC<PaisSelectorProps> = ({
  system,
  label,
  defaultValue,
  onChangeSelected,
  disabled = false,
  placeholder = t('Seleccione o agregue un país...'),
  styles,
  className,
}) => {
  const [options, setOptions] = useState<OptionPais[]>([]);
  const [selectedOption, setSelectedOption] = useState<SingleValue<OptionPais>>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchOptions = async () => {
      setLoading(true);
      try {
        const data = await system.getCompanySystem().getPaises('paises', 'list-paises', 1);
        setOptions(data.map((p) => ({ key: p.key, label: p.text })));
      } catch (err) {
        console.error('Error fetching countries:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    if (defaultValue && options.length > 0) {
      const found = options.find((c) => c.key === defaultValue);
      if (found && found.key !== selectedOption?.key) {
        setSelectedOption(found);
      }
    }
  }, [defaultValue, options]);

  const handleChange = (selected: SingleValue<OptionPais>, actionMeta: ActionMeta<OptionPais>) => {
    switch (actionMeta.action) {
      case 'clear': // Se limpió toda la selección
      case 'remove-value': // Se eliminó la opción
        setSelectedOption(null);
        onChangeSelected(null);
        return;

      case 'create-option': // Se creó una opción nueva
        if (selected) {
          const normalizedLabel = normalizeString(selected.label);

          // Verifica si ya existe (ignorando acentos y mayúsculas)
          const existing = options.find((opt) => normalizeString(opt.label) === normalizedLabel);

          if (existing) {
            setSelectedOption(existing);
            onChangeSelected(existing);
            return;
          }

          // Si no existe, crear nueva opción
          const newOption: OptionPais = { key: '', label: selected.label, new: true };
          setSelectedOption(newOption);
          onChangeSelected(newOption);
        }
        return;

      case 'select-option': // Se seleccionó una opción existente
        if (selected) {
          const exists = options.some((opt) => opt.label.toLowerCase() === selected.label.toLowerCase());
          const newOption: OptionPais = exists ? selected : { key: '', label: selected.label };
          setSelectedOption(newOption);
          onChangeSelected(newOption);
        }
        return;

      default:
        // Otros casos como 'pop-value', 'deselect-option', 'initial-input-focus' los ignoramos por ahora
        return;
    }
  };

  const formatCreateLabel = (inputValue: string = '') => {
    const normalizedInput = normalizeString(inputValue);
    const exists = options.some((opt) => normalizeString(opt.label) === normalizedInput);
    return exists ? undefined : t(`Agregar "${inputValue}"`);
  };

  return (
    <div className={className} style={styles}>
      {label && (
        <label style={{ padding: '5px 0' }}>{label}</label>
      )}
      <CreatableSelect
        isDisabled={disabled}
        isLoading={loading}
        options={options}
        value={selectedOption}
        onChange={handleChange}
        placeholder={loading ? t('Cargando...') : placeholder}
        styles={customStylesReactSelect}
        isClearable
        isSearchable
        filterOption={filterOption}
        formatCreateLabel={formatCreateLabel}
      />
    </div>
  );
};

export default PaisSelector;
